import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
} from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate } from 'react-router-dom';
import { useClinicNotificationsBell } from '../../hooks/useClinicNotificationsBell';

const TYPE_CONFIG = {
  info: { icon: <InfoOutlinedIcon fontSize="small" />, color: 'primary.main' },
  success: { icon: <CheckCircleOutlineIcon fontSize="small" />, color: 'success.main' },
  warning: { icon: <WarningAmberIcon fontSize="small" />, color: 'warning.main' },
  error: { icon: <ErrorOutlineIcon fontSize="small" />, color: 'error.main' },
  SENT: { icon: <CheckCircleOutlineIcon fontSize="small" />, color: 'success.main' },
  PENDING: { icon: <InfoOutlinedIcon fontSize="small" />, color: 'primary.main' },
  FAILED: { icon: <ErrorOutlineIcon fontSize="small" />, color: 'error.main' },
};

const DEFAULT_CONFIG = { icon: <InfoOutlinedIcon fontSize="small" />, color: 'text.secondary' };

export default function NotificationBell({ anchorEl, onOpen, onClose }) {
  const isOpen = Boolean(anchorEl);
  const navigate = useNavigate();

  const { notifications, refetch: getNotifications } = useClinicNotificationsBell();

  useEffect(() => {
    window.addEventListener('notification-read', getNotifications);
    return () => window.removeEventListener('notification-read', getNotifications);
  }, [getNotifications]);

  const handleVerTodas = () => {
    onClose();
    navigate('/clinic-notifications');
  };

  const displayed = notifications.slice(0, 3);

  return (
    <>
      <IconButton size="small" onClick={onOpen}>
        <Badge badgeContent={notifications.length || null} color="error">
          {isOpen ? (
            <NotificationsIcon fontSize="small" />
          ) : (
            <NotificationsOutlinedIcon fontSize="small" />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, borderRadius: 2, mt: 1 } } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Notificações
          </Typography>
        </Box>

        <List disablePadding>
          {displayed.length === 0 ? (
            <ListItem sx={{ py: 3, justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Nenhuma notificação nova.
              </Typography>
            </ListItem>
          ) : (
            displayed.map((notification, index) => {
              const key = notification.type ?? notification.status;
              const config = TYPE_CONFIG[key] ?? DEFAULT_CONFIG;
              return (
                <React.Fragment key={notification.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      py: 1.5,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <Avatar
                        sx={{ width: 32, height: 32, bgcolor: 'transparent', color: config.color }}
                      >
                        {config.icon}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={'bold'}>
                            {notification.description}
                          </Typography>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {notification.content?.length > 60
                              ? `${notification.content.slice(0, 60)}...`
                              : notification.content}
                          </Typography>
                          <Button
                            size="small"
                            sx={{ mt: 0.5, p: 0, minWidth: 0 }}
                            onClick={() => {
                              onClose();
                              navigate('/clinic-notifications', {
                                state: { selectedId: notification.id },
                              });
                            }}
                          >
                            Ler
                          </Button>
                        </>
                      }
                    />
                  </ListItem>
                  {index < displayed.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })
          )}
        </List>

        <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 1.5, textAlign: 'center' }}>
          <Typography
            variant="body2"
            color="primary"
            onClick={handleVerTodas}
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            Ver todas
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
